import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Mail, MapPin, Clock, UserRound, HelpCircle, MessageSquare } from 'lucide-react';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { Sidebar, buildHelpTopics, useContact } from '../../Common/HelpCenter';
import { submitContactForm } from '../../../../services/formsApi';
import Turnstile from '@ajat/design-system/Turnstile';
import { TURNSTILE_SITE_KEY } from '../../../../config/turnstile';
import { CATEGORIES } from './faqData';
import { HELP_LABELS, HELP_ROUTES } from './helpConfig';
import '../../Common/HelpCenter/HelpCenter.css';
import '../../Common/HelpCenter/Contact.css';

const TOPICS = [
  { value: 'order',    label: 'Beställning' },
  { value: 'design',   label: 'Design & tryck' },
  { value: 'delivery', label: 'Leverans' },
  { value: 'payment',  label: 'Betalning' },
  { value: 'school',   label: 'Skolsamarbete' },
  { value: 'other',    label: 'Annat' },
];

const LABELS = {
  required:     'Obligatoriskt',
  invalidEmail: 'Ogiltig e-post',
  submitError:  'Något gick fel. Försök igen.',
};

// TODO: replace with SE office details once available
const CONTACT_DETAILS = {
  address: 'Ny Adelgade 8-10, 1104 Köpenhamn K, Danmark',
  email: 'info@clseifert.com',
  phone: '+45 33 12 02 77',
  hours: 'Mån–fre: 09:00–17:00',
};

const CLSSEContact = () => {
  const navigate = useNavigate();
  const contact = useContact({
    labels: LABELS,
    onSubmit: async ({ data }) => {
      await submitContactForm('clsse', {
        name: data.name,
        email: data.email,
        topic: data.topic,
        topicLabel: TOPICS.find((t) => t.value === data.topic)?.label || data.topic,
        message: data.message,
        company: data.company, // honeypot
        captchaToken: data.captchaToken,
      });
    },
  });

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  useEffect(() => {
    if (contact.submitted) {
      pushNotification({
        type: 'success',
        title: 'Tack för ditt meddelande',
        message: 'Vi återkommer så snart vi kan.',
      });
    }
  }, [contact.submitted]);

  const selectedTopicLabel = TOPICS.find((t) => t.value === contact.data.topic)?.label || '';

  const topics = buildHelpTopics({
    context: 'contact',
    categories: CATEGORIES,
    labels: HELP_LABELS,
    routes: HELP_ROUTES,
    navigate,
  });

  const detailsCard = (
    <div className="contact-page__details-card">
      <h2 className="contact-page__details-title typo-h3">Kontaktuppgifter</h2>
      <ul className="contact-page__details-list">
        <li className="contact-page__detail">
          <span className="contact-page__detail-icon"><MapPin size={18} /></span>
          <div className="contact-page__detail-content">
            <p className="contact-page__detail-label typo-body-sm">Adress</p>
            <p className="contact-page__detail-value typo-body">{CONTACT_DETAILS.address}</p>
          </div>
        </li>
        <li className="contact-page__detail">
          <span className="contact-page__detail-icon"><Mail size={18} /></span>
          <div className="contact-page__detail-content">
            <p className="contact-page__detail-label typo-body-sm">E-post</p>
            <p className="contact-page__detail-value typo-body">
              <a href={`mailto:${CONTACT_DETAILS.email}`}>{CONTACT_DETAILS.email}</a>
            </p>
          </div>
        </li>
        <li className="contact-page__detail">
          <span className="contact-page__detail-icon"><Clock size={18} /></span>
          <div className="contact-page__detail-content">
            <p className="contact-page__detail-label typo-body-sm">Öppettider</p>
            <p className="contact-page__detail-value typo-body">{CONTACT_DETAILS.hours}</p>
          </div>
        </li>
      </ul>
    </div>
  );

  return (
    <div className="helpcenter">
      <Header showTopBar={false} />
      <main className="helpcenter__main">
        <div className="helpcenter__container">
          <div className="helpcenter__list-col">
            <h1 className="helpcenter__title typo-h1">Kontakta oss</h1>
            <p className="helpcenter__lede typo-body-lg">
              Skriv till oss, så svarar vi så snart vi kan.
              {HELP_ROUTES.claims ? (
                <>
                  {' '}För konkreta fel med din order kan du även skicka en{' '}
                  <a href={HELP_ROUTES.claims} className="helpcenter__inline-link">reklamation</a>.
                </>
              ) : null}
            </p>

            {contact.submitted ? (
              <div className="helpcenter__success">
                <span className="helpcenter__success-icon"><Check size={28} /></span>
                <h2 className="helpcenter__success-title typo-h2">Tack för ditt meddelande</h2>
                <p className="helpcenter__success-text typo-body">
                  Vi återkommer via e-post inom 1–2 vardagar.
                </p>
                <div className="helpcenter__success-ctas">
                  <Button variant="black" size="secondary" iconRight={<ArrowRight size={18} />} onClick={() => navigate('/clothing/cls-se')}>
                    Tillbaka till startsidan
                  </Button>
                  <Button variant="outline" size="secondary" onClick={() => contact.reset()}>
                    Skicka ett nytt meddelande
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {detailsCard}
                <form className="contact-page__form-card" onSubmit={contact.handleSubmit} noValidate>
                  {/* Honeypot — hidden from users; bots that fill it are dropped server-side. */}
                  <input
                    type="text"
                    name="company"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={contact.data.company}
                    onChange={contact.update('company')}
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  />
                  <Input
                    type="text"
                    label="Namn"
                    icon={UserRound}
                    value={contact.data.name}
                    onChange={contact.update('name')}
                    onBlur={contact.handleBlur('name')}
                    onFocus={contact.handleFocus('name')}
                    error={contact.fieldError('name')}
                  />
                  <Input
                    type="text"
                    label="E-post"
                    icon={Mail}
                    value={contact.data.email}
                    onChange={contact.update('email')}
                    onBlur={contact.handleBlur('email')}
                    onFocus={contact.handleFocus('email')}
                    error={contact.fieldError('email')}
                  />
                  <Input
                    type="dropdown"
                    label="Ämne"
                    icon={HelpCircle}
                    value={selectedTopicLabel}
                    options={TOPICS}
                    onSelect={(opt) => contact.setField('topic', opt.value)}
                    onBlur={contact.handleBlur('topic')}
                    onFocus={contact.handleFocus('topic')}
                    error={contact.fieldError('topic')}
                  />
                  <Input
                    type="textarea"
                    label="Meddelande"
                    icon={MessageSquare}
                    rows={5}
                    value={contact.data.message}
                    onChange={contact.update('message')}
                    onBlur={contact.handleBlur('message')}
                    onFocus={contact.handleFocus('message')}
                    error={contact.fieldError('message')}
                  />
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onToken={(t) => contact.setField('captchaToken', t)}
                    className="contact-page__turnstile"
                  />
                  {contact.submitError ? (
                    <p className="contact-page__error typo-body-sm">{contact.submitError}</p>
                  ) : null}
                  <Button
                    type="submit"
                    variant="black"
                    size="primary"
                    iconRight={<ArrowRight size={20} />}
                    loading={contact.submitting}
                    disabled={contact.submitting}
                  >
                    Skicka meddelande
                  </Button>
                </form>
              </>
            )}
          </div>

          <Sidebar
            title={HELP_LABELS.sidebarTitle}
            topics={topics}
            activeId="contact"
            dividerBeforeId={HELP_ROUTES.claims ? 'claims' : 'contact'}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CLSSEContact;
